import React, {useContext, useMemo, useState} from "react";
import {CircularProgress, Fade, SvgIcon} from "@mui/material";
import {ReactComponent as HorizontalDragIcon} from "../../../../../../../assets/images/notifications/horizontal-drag.svg";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import {UserHomepageComponentTypes, UserHomepageEventEmitter} from "../../../../models";
import UserHomepageControlPanelBannerEntity from "./types/banner";
import UserHomepageControlPanelCarouselImageEntity from "./types/carousel-image";
import {preventEventPropagation} from "../../../../../../../core/services/utils/utils";
import {UserHomepageControllerContext} from "../../../../index";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import CarouselImageDialog from "../../../../../../components/dialogs/carousel-image";


const UserHomepageControlPanelContent = ({
                                             tabId,
                                             componentType,
                                             componentId,
                                             content,
                                             dragging,
                                             updating,
                                             imageWidth,
                                             imageHeight,
                                         }) => {
    const controller = useContext(UserHomepageControllerContext);
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    /**
     * Invokes the appropriate controller callback for the click of the content card.
     */
    const onContentClicked = () => {
        if (dragging)
            return;
        controller.selectContent(
            tabId,
            componentId,
            content.id,
            UserHomepageEventEmitter.controlPanel,
        );
        if (componentType?.id === UserHomepageComponentTypes.imageCarousel) {
            setEditDialogOpen(true);
        }
    }

    /**
     * Removes the content from the content list of the component.
     */
    const removeContent = () => {
        controller.removeContent(
            tabId,
            componentId,
            content.id,
            false
        )
    }

    /**
     * Gets users confirmation before removing the current content entirely.
     */
    const getRemoveConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure that you want to remove this content?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeContent(),
        );
    }

    const title = useMemo(() => {
        switch (componentType?.id) {
            case UserHomepageComponentTypes.timeBased:
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.largeProduct:
                return (
                    <ProductInformationPopover partNo={content?.partNo}>
                        {content?.partNo ?? ''}
                    </ProductInformationPopover>
                );
            case UserHomepageComponentTypes.category:
                return content?.title ?? '';
            case UserHomepageComponentTypes.slideShow:
            case UserHomepageComponentTypes.banner:
            case UserHomepageComponentTypes.imageCarousel:
                return <></>;
            default:
                return <></>;
        }
    }, [content, componentType])

    const entity = useMemo(() => {
        switch (componentType?.id) {
            case UserHomepageComponentTypes.timeBased:
            case UserHomepageComponentTypes.smallProduct:
            case UserHomepageComponentTypes.mediumProduct:
            case UserHomepageComponentTypes.largeProduct:
            case UserHomepageComponentTypes.category:
                return <></>;
            case UserHomepageComponentTypes.slideShow:
            case UserHomepageComponentTypes.banner:
                return <UserHomepageControlPanelBannerEntity
                    banner={content}
                    updating={updating}
                    tabId={tabId}
                    componentId={componentId}
                />;
            case UserHomepageComponentTypes.imageCarousel:
                return <UserHomepageControlPanelCarouselImageEntity image={content}/>;
            default:
                return <></>;
        }
    }, [content, componentType, componentId, tabId, updating])

    return (
        <>
            <div
                className={'user-homepage-control-panel-content'}
                id={content?.controlPanelElementId}
                onClick={onContentClicked}
            >
                <div className={'content-card-header'}>
                    <Fade in={!updating} className={'mx-1 handle'}>
                        <SvgIcon component={HorizontalDragIcon}/>
                    </Fade>
                    <div className={'content-title part-number-fluid'}>
                        {title}
                    </div>
                    <div className={'d-flex align-items-center justify-content-end flex-grow-1'}>
                        <IconButton
                            onPressed={(e) => preventEventPropagation(e, getRemoveConfirmation)}
                            disabled={updating}
                            color={'paper.error'}
                        >
                            {
                                updating
                                    ? <CircularProgress size={15}/>
                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                            }
                        </IconButton>
                    </div>
                </div>
                {entity}
            </div>
            {
                componentType?.id === UserHomepageComponentTypes.imageCarousel &&
                <CarouselImageDialog
                    open={editDialogOpen}
                    close={() => setEditDialogOpen(false)}
                    mode={'edit'}
                    image={content}
                    imageWidth={imageWidth}
                    imageHeight={imageHeight}
                    onSuccess={({image, imageWidth: nextImageWidth, imageHeight: nextImageHeight}) => {
                        controller.updateContent(tabId, componentId, image, false);
                        controller.updateComponent(tabId, {
                            id: componentId,
                            content: {
                                imageWidth: nextImageWidth,
                                imageHeight: nextImageHeight,
                            },
                        }, false);
                    }}
                />
            }
        </>
    )
}

export default UserHomepageControlPanelContent;
